import { Alert, Button, Form, Input, Layout, Menu, Result, Skeleton, Space, Tag, Typography, message } from "antd";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { clearAdminSession, getAdminSession, getAdminUsername, loginAdmin } from "../shared/api/client";
import type { DashboardData, SuggestionStatus, SyncStatus } from "../shared/api/types";
import { formatDateTime } from "../shared/format";
import { loadDashboard, loadSyncStatus, updateSuggestionStatus } from "../projects/steam/api";

const OverviewPage = lazy(() => import("../projects/steam/pages/OverviewPage").then((module) => ({ default: module.OverviewPage })));
const RecommendationPage = lazy(() => import("../projects/steam/pages/RecommendationPage").then((module) => ({ default: module.RecommendationPage })));
const DataQualityPage = lazy(() => import("../projects/steam/pages/DataQualityPage").then((module) => ({ default: module.DataQualityPage })));
const JobsPage = lazy(() => import("../projects/steam/pages/JobsPage").then((module) => ({ default: module.JobsPage })));
const SuggestionsPage = lazy(() => import("../projects/steam/pages/SuggestionsPage").then((module) => ({ default: module.SuggestionsPage })));
const MiniProgramSettingsPage = lazy(() => import("../projects/steam/pages/MiniProgramSettingsPage").then((module) => ({ default: module.MiniProgramSettingsPage })));
const UsersPage = lazy(() => import("../projects/steam/pages/UsersPage").then((module) => ({ default: module.UsersPage })));

const { Header, Sider, Content } = Layout;
type PageKey = "overview" | "users" | "recommendations" | "quality" | "jobs" | "suggestions" | "settings";
const pages: PageKey[] = ["overview", "users", "recommendations", "quality", "jobs", "suggestions", "settings"];

function pageFromHash(): PageKey {
  const page = location.hash.split("/").filter(Boolean).at(-1) as PageKey;
  return pages.includes(page) ? page : "overview";
}

function LoginForm({ loading, error, onSubmit }: {
  loading: boolean;
  error: string;
  onSubmit: (values: { username: string; password: string }) => Promise<void>;
}) {
  return (
    <Form layout="vertical" onFinish={onSubmit}>
      <Form.Item label="账号" name="username" rules={[{ required: true, message: "请输入管理员账号" }]}>
        <Input autoComplete="username" placeholder="管理员账号" />
      </Form.Item>
      <Form.Item label="密码" name="password" rules={[{ required: true, message: "请输入管理员密码" }]}>
        <Input.Password autoComplete="current-password" placeholder="管理员密码" />
      </Form.Item>
      {error ? <Alert className="login-error" type="error" showIcon message={error} /> : null}
      <Button block type="primary" htmlType="submit" loading={loading}>登录</Button>
    </Form>
  );
}

export default function AdminApp() {
  const [authenticated, setAuthenticated] = useState(Boolean(getAdminSession()));
  const [username, setUsername] = useState(getAdminUsername());
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [page, setPage] = useState<PageKey>(pageFromHash());
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState("");
  const [syncStatusError, setSyncStatusError] = useState("");
  const [savingSuggestion, setSavingSuggestion] = useState("");
  const [usersRefreshVersion, setUsersRefreshVersion] = useState(0);
  const refreshingRef = useRef(false);

  const refresh = useCallback(async (silent = false) => {
    if (!getAdminSession() || refreshingRef.current) return;
    refreshingRef.current = true;
    if (!silent) setLoading(true);
    try {
      const [dashboardResult, syncStatusResult] = await Promise.allSettled([loadDashboard(), loadSyncStatus()]);
      if (dashboardResult.status === "fulfilled") {
        setDashboard(dashboardResult.value);
        setDashboardError("");
      } else {
        setDashboardError(dashboardResult.reason instanceof Error ? dashboardResult.reason.message : "业务数据加载失败");
      }
      if (syncStatusResult.status === "fulfilled") {
        setSyncStatus(syncStatusResult.value);
        setSyncStatusError("");
      } else {
        setSyncStatusError(syncStatusResult.reason instanceof Error ? syncStatusResult.reason.message : "任务状态加载失败");
      }
    } finally {
      refreshingRef.current = false;
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const onHashChange = () => setPage(pageFromHash());
    window.addEventListener("hashchange", onHashChange);
    if (!location.hash) location.hash = "/projects/steam/overview";
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    const expire = () => {
      setAuthenticated(false);
      setUsername("");
      setDashboard(null);
      setSyncStatus(null);
      setDashboardError("");
      setSyncStatusError("");
      setLoginError("登录已过期，请重新登录");
    };
    window.addEventListener("admin-auth-expired", expire);
    return () => window.removeEventListener("admin-auth-expired", expire);
  }, []);

  useEffect(() => {
    if (!authenticated) return;
    void refresh();
    const refreshWhenVisible = () => {
      if (!document.hidden) void refresh(true);
    };
    const timer = window.setInterval(refreshWhenVisible, 60_000);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [authenticated, refresh]);

  const login = async (values: { username: string; password: string }) => {
    setLoginLoading(true);
    setLoginError("");
    try {
      const session = await loginAdmin(values.username, values.password);
      setUsername(session.username || values.username.trim());
      setAuthenticated(true);
      setDashboard(null);
      setSyncStatus(null);
      setDashboardError("");
      setSyncStatusError("");
    } catch (requestError) {
      setLoginError(requestError instanceof Error ? requestError.message : "登录失败");
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = () => {
    clearAdminSession();
    setAuthenticated(false);
    setUsername("");
    setDashboard(null);
    setSyncStatus(null);
    setDashboardError("");
    setSyncStatusError("");
  };

  const changeSuggestionStatus = async (id: string, status: SuggestionStatus) => {
    setSavingSuggestion(id);
    try {
      await updateSuggestionStatus(id, status);
      setDashboard((current) => current ? {
        ...current,
        suggestions: current.suggestions.map((item) => item.id === id ? { ...item, status } : item),
        overview: {
          ...current.overview,
          pendingSuggestions: current.suggestions.filter((item) =>
            (item.id === id ? status : item.status) === "pending"
          ).length
        }
      } : current);
      message.success("建议状态已更新");
    } catch (requestError) {
      message.error(requestError instanceof Error ? requestError.message : "更新失败");
    } finally {
      setSavingSuggestion("");
    }
  };

  if (!authenticated) {
    return (
      <div className="login-page">
        <div className="login-ambient" />
        <div className="login-panel">
          <Tag color="blue">STEAM RECOMMENDATION OPS</Tag>
          <Typography.Title>管理看板</Typography.Title>
          <Typography.Paragraph type="secondary">查看推荐效果、数据质量、后台任务和系统建议。</Typography.Paragraph>
          <LoginForm loading={loginLoading} error={loginError} onSubmit={login} />
        </div>
      </div>
    );
  }

  const content = () => {
    if (page === "settings") return <MiniProgramSettingsPage />;
    if (page === "users") return <UsersPage refreshVersion={usersRefreshVersion} />;
    if (page === "jobs") {
      if (syncStatus) return <JobsPage data={syncStatus} />;
      if (!syncStatusError) return <Skeleton active paragraph={{ rows: 10 }} />;
      return <Result status="warning" title="暂时无法读取任务状态" subTitle={syncStatusError} extra={<Button type="primary" onClick={() => void refresh()}>重试</Button>} />;
    }
    if (!dashboard) {
      if (!dashboardError) return <Skeleton active paragraph={{ rows: 10 }} />;
      return <Result status="warning" title="暂时无法读取业务数据" subTitle={dashboardError} extra={<Button type="primary" onClick={() => void refresh()}>重试</Button>} />;
    }
    if (page === "recommendations") return <RecommendationPage data={dashboard} />;
    if (page === "quality") return <DataQualityPage data={dashboard} />;
    if (page === "suggestions") return <SuggestionsPage items={dashboard.suggestions} savingId={savingSuggestion} onStatusChange={changeSuggestionStatus} />;
    return <OverviewPage data={dashboard} />;
  };

  const pageError = page === "jobs" ? syncStatusError : page === "settings" || page === "users" ? "" : dashboardError;
  const hasPageData = page === "jobs" ? Boolean(syncStatus) : page === "settings" || page === "users" || Boolean(dashboard);

  return (
    <Layout className="admin-layout">
      <Sider width={244} breakpoint="lg" collapsedWidth="0" className="admin-sider">
        <div className="brand"><span className="brand-mark">S</span><div><strong>Steam 推荐</strong><small>管理控制台</small></div></div>
        <div className="project-label">当前项目</div>
        <div className="project-chip"><span className="project-dot" /><div><strong>Steam 微信小程序</strong><small>production</small></div></div>
        <Menu
          mode="inline"
          theme="dark"
          selectedKeys={[page]}
          items={[
            { key: "overview", label: "总览" },
            { key: "users", label: "用户列表" },
            { key: "recommendations", label: "推荐效果" },
            { key: "quality", label: "数据质量" },
            { key: "jobs", label: "后台任务" },
            { key: "suggestions", label: <span>建议事项 {dashboard?.overview.pendingSuggestions ? <Tag color="gold">{dashboard.overview.pendingSuggestions}</Tag> : null}</span> },
            { key: "settings", label: "小程序设置" }
          ]}
          onSelect={({ key }) => { location.hash = `/projects/steam/${key}`; }}
        />
        <div className="sider-foot">数据每 60 秒自动刷新</div>
      </Sider>
      <Layout>
        <Header className="admin-header">
          <div><Typography.Text type="secondary">Steam 微信小程序</Typography.Text><Typography.Title level={4}>{page === "overview" ? "业务总览" : page === "users" ? "用户列表" : page === "recommendations" ? "推荐效果" : page === "quality" ? "数据质量" : page === "jobs" ? "后台任务" : page === "settings" ? "小程序设置" : "建议事项"}</Typography.Title></div>
          <Space>
            {page === "users" ? null : <Typography.Text type="secondary" className="update-time">更新于 {formatDateTime(page === "jobs" ? syncStatus?.generatedAt : dashboard?.generatedAt)}</Typography.Text>}
            <Typography.Text type="secondary" className="admin-user">{username}</Typography.Text>
            <Button loading={page === "users" ? false : loading} onClick={() => page === "users" ? setUsersRefreshVersion((version) => version + 1) : void refresh()}>刷新</Button>
            <Button onClick={logout}>退出登录</Button>
          </Space>
        </Header>
        <Content className="admin-content">
          {pageError && hasPageData ? <Alert className="content-row" type="warning" showIcon message="本次刷新失败，当前显示上次成功数据" description={pageError} action={<Button size="small" onClick={() => void refresh()}>重试</Button>} /> : null}
          <Suspense fallback={<Skeleton active paragraph={{ rows: 10 }} />}>{content()}</Suspense>
        </Content>
      </Layout>
    </Layout>
  );
}
